import Location from "../../../hooks/Location";
import {
  useFirestore,
  useFirestoreGeneral,
  useFirestoreGeneralOrderBy,
  useFirestoreIn,
  useFirestoreOrderBy
} from '../../../firebase/useFirestore';
import PageHeader from "../../../components/layout/PageHeader.jsx";
import Tooltip from '../../../components/common/Tooltip';
import { db, timestamp } from '../../../firebase/config';
import { v4 as uuid } from 'uuid';
import { client } from "../../../hooks/Client";
import plusButton from '../../../images/icons/plus-icon.png';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useState, useEffect, useMemo } from 'react';
import DeleteModal from '../../../components/common/DeleteModal';
import useSettings from "../../../hooks/Settings";
import FieldTypeConfig from "../../../components/common/ActivityFieldTypeConfig.jsx";
import AddItemTableRow from "../../../components/common/AddItemTableRow.jsx";
import Modal from "../../../components/common/Modal.jsx";
import ActivityMeta from "../../../components/activities/ActivityMeta.jsx";
import clickableRowProps from '../../../helpers/a11y/clickableRowProps';

const EventBuilder = () => {
  // State
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [docid, setDocid] = useState('');
  const [dragArray, setDragArray] = useState([]);
  const [deleteFieldModal, setDeleteFieldModal] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState(null);
  const [editQuestion, setEditQuestion] = useState(null);
  const [activityId, setActivityId] = useState('');
  const [activityTargetgroups, setActivityTargetgroups] = useState();
  const [showAddContainer, setShowAddContainer] = useState(false);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [existingFieldsModalOpen, setExistingFieldsModalOpen] = useState(false);
  const [selectedCustomEventId, setSelectedCustomEventId] = useState(null);
  const [selectedExistingFieldIds, setSelectedExistingFieldIds] = useState([]);

  // Hooks
  const eventId = Location()[3];
  const secundairyColor = useSettings().SecundairyColor;

  // Firestore
  const events = useFirestoreGeneral('EventForms', 'ID', eventId || '');
  const allEvents = useFirestoreOrderBy('EventForms', 'Position', 'asc');
  const eventFields = useFirestoreGeneralOrderBy(
    'EventFields',
    'EventId',
    eventId || '',
    'Position',
    'asc'
  );
  const allEventFields = useFirestoreOrderBy('EventFields', 'Position', 'asc');
  const outputsRaw = useFirestoreGeneralOrderBy(
    'Outputs',
    'ActivityID',
    activityId || '',
    'Position',
    'asc'
  );
  // Hide soft-deleted outputs (ADR-008)
  const outputs = useMemo(() => (outputsRaw || []).filter((o) => !o.Deleted), [outputsRaw]);
  const personas = useFirestoreGeneral('Personas', 'Status', 'active');
  const activities = useFirestoreGeneral('Activities', 'ID', activityId || '');
  const targetgroups = useFirestoreIn(
    'Stakeholders',
    'ID',
    activityTargetgroups || [{ ID: '', Name: '' }]
  );
  const customEvents = useFirestore('CustomEventFields');

  // Set event data
  useEffect(() => {
    events && events.forEach(event => {
      setTitle(event.Title || '');
      setDescription(event.Description || '');
      setDocid(event.docid || '');
      setActivityId(event.ActivityID || '');
    });
  }, [events]);

  // Set activity targetgroup
  useEffect(() => {
    activities && activities.forEach(activity => {
      setActivityTargetgroups(activity.Targetgroup || [{ ID: '', Name: '' }]);
    });
  }, [activities]);

  // Keep drag list of fields in sync
  useEffect(() => {
    setDragArray(eventFields || []);
  }, [eventFields]);

  // Add event field
  const addField = async () => {
    await db.collection('EventFields').add({
      EventId: eventId,
      Type: null,
      ID: uuid(),
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      Title: `Nieuw veld ${eventFields?.length + 1}`,
      Position: (eventFields?.length || 0) + 1
    });
  };

  // Title handler (event meta)
  const titleHandler = (e) => {
    const value = e.target.value;
    setTitle(value);
    db.collection('EventForms').doc(docid).update({ Title: value });
  };

  // Description handler (event meta)
  const descriptionHandler = (e) => {
    const value = e.target.value;
    setDescription(value);
    db.collection('EventForms').doc(docid).update({ Description: value });
  };

  // Edit field (toggle)
  const editField = (e) => {
    const id = e.currentTarget.dataset.id;
    setEditQuestion(prev => (prev !== id ? id : null));
  };

  // Duplicate field
  const duplicateField = async (field) => {
    const duplicateData = {
      EventId: field.EventId,
      Type: field.Type ?? null,
      ID: uuid(),
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      Title: `Kopie van ${field.Title || 'veld'}`,
      Position: (eventFields?.length || 0) + 1
    };

    await db.collection('EventFields').add({
      ...duplicateData
    });
  };

  // Eerst bevestigen: één klik verwijderde een invulveld meteen, terwijl
  // eerder gegeven antwoorden ernaar blijven verwijzen.
  const askDeleteField = (field) => {
    if (!field?.docid) return;
    setFieldToDelete(field);
    setDeleteFieldModal(true);
  };

  const deleteField = async () => {
    if (!fieldToDelete?.docid) return;
    await db.collection('EventFields').doc(fieldToDelete.docid).delete();
  };

  // DnD: update order of fields
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArray);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setDragArray(items);

    // Persist new positions
    items.forEach((item, idx) => {
      db.collection('EventFields').doc(item.docid).update({ Position: idx + 1 });
    });
  };

  // Type handler
  const typeHandler = (e) => {
    const value = e.target.options[e.target.selectedIndex].value;
    const fieldDocId = e.target.dataset.docid;

    db.collection('EventFields').doc(fieldDocId).update({ Type: value });
  };

  // Field title handler 
  const fieldTitleHandler = (e) => {
    const value = e.target.value;
    const fieldDocId = e.target.dataset.docid;

    db.collection('EventFields').doc(fieldDocId).update({ Title: value });
  };

  // Add custom field
  const addCustomField = async () => {
    if (!selectedCustomEventId) return;

    const customEvent = customEvents.find(
      (event) => event.ID === selectedCustomEventId
    );
    if (!customEvent) return;

    const newEventFieldId = uuid();

    await db.collection('EventFields').add({
      EventId: eventId,
      Type: customEvent.Type || null,
      ID: newEventFieldId,
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      Title: customEvent.Title || 'Custom veld',
      Position: (eventFields?.length || 0) + 1,
      CustomEventId: customEvent.ID,
    });

    // Copy multiple options if applicable
    if (
      customEvent.Type === 'multiple-one' ||
      customEvent.Type === 'multiple-multiple'
    ) {
      const multipleOptions = await db
        .collection('MultipleQuestionOptions')
        .where('Field', '==', customEvent.ID)
        .get();

      multipleOptions.forEach(async (optionDoc) => {
        const optionData = optionDoc.data();
        await db.collection('MultipleQuestionOptions').add({
          Option: optionData.Option || '',
          Question: optionData.Question || '',
          Field: newEventFieldId,
          Compagny: client,
          CompagnyID: client,
          Timestamp: timestamp,
          Position: optionData.Position || 0,
          ID: uuid(),
          Color: secundairyColor,
        });
      });
    }

    // Close modal and reset selection

    setCustomModalOpen(false);
    setSelectedCustomEventId(null);
  };

  const toggleExistingFieldSelection = (fieldId) => {
    setSelectedExistingFieldIds((prev) => (
      prev.includes(fieldId)
        ? prev.filter((id) => id !== fieldId)
        : [...prev, fieldId]
    ));
  };

  const addExistingFields = async () => {
    if (!selectedExistingFieldIds.length) return;

    const selectedFields = (allEventFields || []).filter(
      (field) => selectedExistingFieldIds.includes(field.ID)
    );

    for (const [index, field] of selectedFields.entries()) {
      const newFieldId = uuid();
      const {
        docid,
        ID,
        EventId,
        Position,
        Compagny,
        CompagnyID,
        Timestamp,
        ...fieldData
      } = field;

      await db.collection('EventFields').add({
        ...fieldData,
        EventId: eventId,
        ID: newFieldId,
        Position: (eventFields?.length || 0) + index + 1,
        Compagny: client,
        CompagnyID: client,
        Timestamp: timestamp,
      });

      if (
        field.Type === 'multiple-one' ||
        field.Type === 'multiple-multiple'
      ) {
        const multipleOptions = await db
          .collection('MultipleQuestionOptions')
          .where('Field', '==', field.ID)
          .get();

        for (const optionDoc of multipleOptions.docs) {
          const optionData = optionDoc.data();

          await db.collection('MultipleQuestionOptions').add({
            ...optionData,
            Field: newFieldId,
            ID: uuid(),
            Compagny: client,
            CompagnyID: client,
            Timestamp: timestamp,
          });
        }
      }
    }

    setExistingFieldsModalOpen(false);
    setSelectedExistingFieldIds([]);
  };

  const eventActivityById = (allEvents || []).reduce((acc, event) => {
    acc[event.ID] = event.ActivityID;
    return acc;
  }, {});

  const existingFields = (allEventFields || [])
    .filter((field) => field.EventId !== eventId)
    .filter((field) =>
      field.Type !== 'date' &&
      field.Type !== 'outputs' &&
      field.Type !== 'participants' &&
      field.Type !== 'description'
    )
    .map((field) => {
      const linkedActivityId = eventActivityById[field.EventId] || '';

      return {
        ...field,
        linkedActivityId,
      };
    });

  const customFieldModalContent = (
    <div className="custom-events-select-container">
      {customEvents && customEvents.length > 0 ? (
        customEvents.map((customEvent) => (
          <label
            key={customEvent.ID}
            className="custom-event-option"
          >
            <input
              type="radio"
              name="customEvent"
              value={customEvent.ID}
              checked={selectedCustomEventId === customEvent.ID}
              onChange={() => setSelectedCustomEventId(customEvent.ID)}
            />
            <span>{customEvent.Title || '(zonder titel)'}</span>
          </label>
        ))
      ) : (
        <p>Er zijn geen custom velden beschikbaar.</p>
      )}
    </div>
  );
  const existingFieldsModalContent = (
    <div className="existing-fields-modal-container">
      {existingFields.length > 0 ? (
        existingFields.map((field) => (
          <label key={field.ID} className="existing-field-option">
            <input
              type="checkbox"
              checked={selectedExistingFieldIds.includes(field.ID)}
              onChange={() => toggleExistingFieldSelection(field.ID)}
            />
            <div className="existing-field-option-text">
              <span>
                {field.Title || '(zonder titel)'} ({field.linkedActivityId ? <ActivityMeta id={field.linkedActivityId} inline /> : 'Zonder activiteit'})
              </span>
            </div>
          </label>
        ))
      ) : (
        <p>Er zijn geen bestaande velden beschikbaar.</p>
      )}
    </div>
  );

  return (
    <div className="main">
      <div className="main-container">
        <PageHeader
          heading={'Formulier aanpassen'}
          description={`Op deze pagina kun je de instellingen van dit formulier aanpassen.`}
          AI={false}
        />

        <div id='eventbuilder-main-container'>
          <p><b>Titel formulier</b></p>
          <input
            type="text"
            placeholder='Titel'
            value={title}
            onChange={titleHandler}
          />

          <p><b>Omschrijving</b></p>
          <textarea
            id="questionnaire-builder-description-textarea"
            cols="30"
            rows="10"
            placeholder='Voeg een omschrijving toe van je vragenlijst'
            value={description}
            onChange={descriptionHandler}
          /> 

          <p><b>Velden toevoegen</b></p>

          <div className="event-fields-container">
            {/* Drag & Drop list of fields */}
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="eventfields" direction="vertical">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {dragArray && dragArray.map((field, index) => (
                      <Draggable key={field.ID} draggableId={field.ID} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className="order-questionnaire-field-item-container"
                            style={{
                              backgroundColor: snapshot.isDragging
                                ? 'rgba(0,0,0,0.02)'
                                : 'transparent',
                              ...provided.draggableProps.style
                            }}
                          >
                            <div id='order-fields-item-container'>
                              <div id='section-order-field-container'>

                                {/* Drag handle */}
                                <div
                                  style={{ display: 'flex', alignItems: 'center', marginRight: 8 }}
                                  {...provided.dragHandleProps}
                                >
                                  <DragIndicatorOutlinedIcon />
                                </div>

                                {/* Inline title input */}
                                <input
                                  type="text"
                                  defaultValue={field.Title || ''}
                                  data-docid={field.docid}
                                  onChange={fieldTitleHandler}
                                  placeholder="Veld titel"
                                  style={{ flex: 1 }}
                                />

                                {/* Actions */}
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                  <Tooltip content="Aanpassen" width="30px">
                                    <EditOutlinedIcon
                                      id='remove-field-from-section-icon'
                                      className='table-delete-icon'
                                      data-id={field.ID}
                                      onClick={editField}
                                    />
                                  </Tooltip>
                                  <Tooltip content="Kopiëren" width="30px">
                                    <ContentCopyOutlinedIcon
                                      id='remove-field-from-section-icon'
                                      className='table-delete-icon'
                                      onClick={() => duplicateField(field)}
                                    />
                                  </Tooltip>

                                  <Tooltip content="Verwijderen" width="30px">
                                    <DeleteOutlinedIcon
                                      id='remove-field-from-section-icon'
                                      className='table-delete-icon'
                                      onClick={() => askDeleteField(field)}
                                    />
                                  </Tooltip>
                                </div>
                              </div>

                              {editQuestion === field.ID && (
                                <div className="edit-field-container">
                                  <div id='event-builder-edit-field-select-container'>
                                    <select
                                      value={field.Type || ''}
                                      data-docid={field.docid}
                                      onChange={typeHandler}
                                    >
                                      <option value="">-- Selecteer een type --</option>
                                      <option value="date">Datum</option>
                                      <option value="outputs">Outputs</option>
                                      <option value="targetgroups">Doelgroep</option>
                                      <option value="open">Open vraag</option>
                                      <option value="scale">Schaal vraag</option>
                                      <option value="number">Numerieke vraag</option>
                                      <option value="multiple-one">Meerkeuze vraag (één keuze)</option>
                                      <option value="multiple-multiple">Meerkeuze vraag (meerdere keuzes)</option>
                                    </select>
                                  </div>

                                  <div>
                                    <FieldTypeConfig
                                      field={{ ...field }}
                                      fieldCollection="EventFields"
                                      personas={personas}
                                      outputs={outputs}
                                      targetgroups={targetgroups}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {/* Add new field button */}
             <AddItemTableRow toolTipContent={'Eigen veld toevoegen'} onClick={() => setShowAddContainer(true)}/>
              <Modal
                modalOpen={showAddContainer}
                setModalOpen={setShowAddContainer}
                pageHeader={'Veld toevoegen'}
                hideConfirm={true}
                content={
                  <>
                    <div className="add-options-container-item-container" {...clickableRowProps(() => { setShowAddContainer(false); addField(); })}>
                      <img src={plusButton} alt="" />
                      <p>Eigen veld toevoegen</p>
                    </div>
                    <div className="add-options-container-item-container" {...clickableRowProps(() => { setShowAddContainer(false); setExistingFieldsModalOpen(true); })}>
                      <img src={plusButton} alt="" />
                      <p>Bestaande velden toevoegen</p>
                    </div>
                    {customEvents && customEvents.length > 0 &&
                      <div className="add-options-container-item-container" {...clickableRowProps(() => { setShowAddContainer(false); setCustomModalOpen(true); })}>
                        <img src={plusButton} alt="" />
                        <p>Custom veld toevoegen</p>
                      </div>
                    }
                  </>
                }
              />
          </div>
        </div>
      </div>
      <Modal
        modalOpen={customModalOpen}
        setModalOpen={setCustomModalOpen}
        confirm={addCustomField}
        pageHeader={'Selecteer custom veld'}
        content={customFieldModalContent}
      />
      <Modal
        modalOpen={existingFieldsModalOpen}
        setModalOpen={setExistingFieldsModalOpen}
        confirm={addExistingFields}
        pageHeader={'Selecteer bestaande velden'}
        content={existingFieldsModalContent}
      />
      <DeleteModal
        deleteModal={deleteFieldModal}
        setDeleteModal={setDeleteFieldModal}
        deleteItem={deleteField}
        deleteName={fieldToDelete?.Title || 'dit invulveld'}
      />
    </div>
  );
};

export default EventBuilder;
